import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import ThemeToggle from "@/components/ThemeToggle";
import {
  MessageSquare, Users, Settings, ChevronLeft, LogOut, Trash2, Plus, Edit,
  MessageCircle, Key, Activity, RefreshCw, Shield, Save, Eye, EyeOff, ArrowLeft,
  Cpu, CheckCircle2, XCircle, Loader2, Wifi, WifiOff, Package,
  CreditCard, Zap, BarChart3, Server, Gauge, Timer, TrendingUp
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ title, value, icon: Icon, color = "primary" }) {  return (
    <Card className="border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>{value ?? "—"}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon className="h-6 w-6 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── USER DIALOG ──────────────────────────────────────────────────────────
function UserDialog({ open, onClose, onSave, editUser = null }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "user", credits: 100, message_limit: -1, is_active: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editUser) {
      setForm({
        name: editUser.name || "",
        email: editUser.email || "",
        password: "",
        role: editUser.role || "user",
        credits: editUser.credits ?? 100,
        message_limit: editUser.message_limit ?? -1,
        is_active: editUser.is_active !== false,
      });
    } else {
      setForm({ name: "", email: "", password: "", role: "user", credits: 100, message_limit: -1, is_active: true });
    }
  }, [editUser, open]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{editUser ? "Upravit uživatele" : "Vytvořit uživatele"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" data-testid="user-name-input" />
          </div>
          {!editUser && (
            <>
              <div className="space-y-1.5">
                <Label>E-mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="uzivatel@firma.cz" data-testid="user-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Heslo</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" data-testid="user-password-input" />
              </div>
            </>
          )}
          {editUser && (
            <div className="space-y-1.5">
              <Label>Nové heslo (ponechte prázdné pro zachování)</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" data-testid="user-new-password-input" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Kredity (-1 = neomezeno)</Label>
              <Input type="number" value={form.credits} onChange={(e) => setForm({ ...form, credits: parseInt(e.target.value) || 0 })} data-testid="user-credits-input" />
            </div>
          </div>
          {editUser && (
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="user-active-switch" />
              <Label>Účet aktivní</Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Zrušit</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-primary hover:bg-primary/90" data-testid="user-save-button">
            {loading ? <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> : "Uložit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── CONVERSATION DETAIL DIALOG ─────────────────────────────────────────
function ConvDetailDialog({ conv, onClose }) {
  if (!conv) return null;
  return (
    <Dialog open={!!conv} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="conv-detail-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">{conv.title || "Conversation"}</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto space-y-3 pr-1">
          {(conv.messages || []).map((msg, i) => (
            <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : ""}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                msg.role === "user"
                  ? "bg-primary/10 border border-primary/20"
                  : "bg-muted"
              }`}>
                <div className="text-xs text-muted-foreground mb-1 capitalize font-medium">{msg.role}</div>
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── MODULE CARD ──────────────────────────────────────────────────────────
const PROVIDER_COLORS = {
  openai: "from-green-500/20 to-emerald-500/10 border-green-500/30",
  anthropic: "from-orange-500/20 to-amber-500/10 border-orange-500/30",
  gemini: "from-blue-500/20 to-cyan-500/10 border-blue-500/30",
  ollama: "from-purple-500/20 to-violet-500/10 border-purple-500/30",
};
const PROVIDER_LABELS = { openai: "OpenAI", anthropic: "Anthropic", gemini: "Google", ollama: "Local" };

function ModuleCard({ module, onSave, onTest }) {
  const [enabled, setEnabled] = useState(module.enabled);
  const [useEmergent, setUseEmergent] = useState(module.use_emergent_key !== false);
  const [apiKey, setApiKey] = useState(module.api_key || "");
  const [ollamaUrl, setOllamaUrl] = useState(module.ollama_url || "http://localhost:11434");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const isOllama = module.module_id === "ollama";
  const colorClass = PROVIDER_COLORS[module.module_id] || "";

  // Sync state when parent re-fetches module data
  useEffect(() => {
    setEnabled(module.enabled);
    setUseEmergent(module.use_emergent_key !== false);
    setApiKey(module.api_key || "");
    setOllamaUrl(module.ollama_url || "http://localhost:11434");
  }, [module]);

  const handleSave = async () => {
    setSaving(true);
    const update = { enabled };
    if (isOllama) { update.ollama_url = ollamaUrl; }
    else { update.use_emergent_key = useEmergent; if (!useEmergent) update.api_key = apiKey; }
    await onSave(update);
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    await onTest();
    setTesting(false);
  };

  return (
    <Card className={`border bg-gradient-to-br ${colorClass} transition-all`} data-testid={`module-card-${module.module_id}`}>
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-background/60 border border-border flex items-center justify-center text-base font-bold">
              {module.module_id === "openai" && "⬡"}
              {module.module_id === "anthropic" && "✦"}
              {module.module_id === "gemini" && "◈"}
              {module.module_id === "ollama" && "◎"}
            </div>
            <div>
              <h3 className="font-semibold text-sm">{module.name}</h3>
              <Badge variant="outline" className="text-xs mt-0.5">{PROVIDER_LABELS[module.module_id]}</Badge>
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid={`module-enable-${module.module_id}`}
          />
        </div>

        <p className="text-xs text-muted-foreground mb-4">{module.description}</p>

        {/* Models list */}
        {!isOllama && module.models?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {module.models.map((m) => (
              <Badge key={m.id} variant="secondary" className="text-xs font-mono">{m.name}</Badge>
            ))}
          </div>
        )}

        {/* Config */}
        {enabled && (
          <div className="space-y-3 mt-3 pt-3 border-t border-border/50">
            {isOllama ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Ollama Server URL</Label>
                <Input
                  value={ollamaUrl}
                  onChange={(e) => setOllamaUrl(e.target.value)}
                  placeholder="http://localhost:11434"
                  className="h-8 text-xs"
                  data-testid="ollama-url-field"
                />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Switch checked={useEmergent} onCheckedChange={setUseEmergent} className="scale-75" data-testid={`use-emergent-${module.module_id}`} />
                  <Label className="text-xs">Použít Emergent Universal Key</Label>
                </div>
                {!useEmergent && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Vlastní API klíč</Label>
                    <div className="relative">
                      <Input
                        type={showKey ? "text" : "password"}
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={module.module_id === "openai" ? "sk-..." : module.module_id === "anthropic" ? "sk-ant-..." : "AIzaSy..."}
                        className="h-8 text-xs pr-8"
                        data-testid={`api-key-${module.module_id}`}
                      />
                      <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {showKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <Button size="sm" className="flex-1 h-7 text-xs bg-primary hover:bg-primary/90 gap-1" onClick={handleSave} disabled={saving} data-testid={`save-module-${module.module_id}`}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Uložit
          </Button>
          {enabled && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={handleTest} disabled={testing} data-testid={`test-module-${module.module_id}`}>
              {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wifi className="h-3 w-3" />}
              Test
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── ADMIN PAGE ──────────────────────────────────────────────────────────
export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [modules, setModules] = useState([]);
  const [payments, setPayments] = useState([]);
  const [serverInfo, setServerInfo] = useState(null);
  const [perfSettings, setPerfSettings] = useState({
    ollama_num_threads: 8, ollama_num_ctx: 2048, ollama_num_predict: 1024,
    ollama_temperature: 0.7, ollama_repeat_penalty: 1.1, openai_max_tokens: 2048, preset: "balanced",
  });
  const [perfSaving, setPerfSaving] = useState(false);
  const [benchmarkResults, setBenchmarkResults] = useState(null);
  const [benchmarking, setBenchmarking] = useState(false);
  const [settings, setSettings] = useState({
    openai_api_key: "", use_emergent_key: true,
    ollama_url: "http://localhost:11434", ollama_enabled: false,
    default_model: "gpt-4o", default_provider: "openai",
  });
  const [userDialog, setUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");

  const fetchModules = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/modules`, { credentials: "include" });
      if (res.ok) setModules(await res.json());
    } catch {}
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/payments`, { credentials: "include" });
      if (res.ok) setPayments(await res.json());
    } catch {}
  }, []);

  const fetchServerInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/server-info`, { credentials: "include" });
      if (res.ok) setServerInfo(await res.json());
    } catch {}
  }, []);

  const fetchPerfSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/performance`, { credentials: "include" });
      if (res.ok) setPerfSettings(await res.json());
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/users`, { credentials: "include" });
      if (res.ok) setUsers(await res.json());
    } catch {}
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/conversations`, { credentials: "include" });
      if (res.ok) setConversations(await res.json());
    } catch {}
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/settings`, { credentials: "include" });
      if (res.ok) setSettings(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchSettings();
    fetchModules();
    fetchServerInfo();
    fetchPerfSettings();
  }, [fetchStats, fetchUsers, fetchSettings, fetchModules, fetchServerInfo, fetchPerfSettings]);

  useEffect(() => {
    if (activeTab === "conversations") fetchConversations();
    if (activeTab === "payments") fetchPayments();
  }, [activeTab, fetchConversations, fetchPayments]);

  const handleCreateUser = async (form) => {
    const res = await fetch(`${API}/admin/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(form),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Failed to create user");
    }
    toast.success("User created");
    fetchUsers();
    fetchStats();
  };

  const handleUpdateUser = async (form) => {
    const payload = {};
    if (form.name) payload.name = form.name;
    if (form.role) payload.role = form.role;
    if (form.credits !== undefined) payload.credits = form.credits;
    if (form.is_active !== undefined) payload.is_active = form.is_active;
    if (form.password) payload.password = form.password;

    const res = await fetch(`${API}/admin/users/${editingUser.user_id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Failed to update user");
    }
    toast.success("User updated");
    fetchUsers();
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    const res = await fetch(`${API}/admin/users/${userId}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      toast.success("User deleted");
      fetchUsers();
      fetchStats();
    }
  };

  const handleUpdateModule = async (moduleId, update) => {
    const res = await fetch(`${API}/admin/modules/${moduleId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(update),
    });
    if (res.ok) {
      toast.success("Module saved");
      fetchModules();
    } else {
      const err = await res.json();
      toast.error(err.detail || "Failed to save module");
    }
  };

  const handleTestModule = async (moduleId) => {
    const res = await fetch(`${API}/admin/modules/${moduleId}/test`, { credentials: "include" });
    const data = await res.json();
    if (data.status === "ok") {
      toast.success(`Connected! ${data.models ? `${data.count} model(s) found.` : "API key valid."}`);
      fetchModules(); // Refresh to get updated Ollama models
    } else {
      toast.error(`Connection failed: ${data.message}`);
    }
    return data;
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(settings),
      });
      if (res.ok) toast.success("Nastavení uloženo");
      else toast.error("Chyba při ukládání");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSavePerf = async () => {
    setPerfSaving(true);
    try {
      const res = await fetch(`${API}/admin/performance`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(perfSettings),
      });
      if (res.ok) { toast.success("Nastavení výkonu uloženo!"); fetchPerfSettings(); }
      else toast.error("Chyba při ukládání výkonu");
    } finally {
      setPerfSaving(false);
    }
  };

  const handleApplyPreset = async (preset) => {
    setPerfSaving(true);
    try {
      const res = await fetch(`${API}/admin/performance/preset/${preset}`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setPerfSettings(data.settings);
        toast.success(`Preset "${preset}" aplikován!`);
      }
    } finally {
      setPerfSaving(false);
    }
  };

  const handleBenchmark = async () => {
    setBenchmarking(true);
    setBenchmarkResults(null);
    try {
      const res = await fetch(`${API}/admin/performance/benchmark`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setBenchmarkResults(data);
        if (data.results?.length > 0) toast.success("Benchmark dokončen!");
        else toast.error(data.error || "Benchmark selhal");
      }
    } catch { toast.error("Chyba benchmarku"); }
    finally { setBenchmarking(false); }
  };

  const openConvDetail = async (convId) => {
    const res = await fetch(`${API}/admin/conversations/${convId}`, { credentials: "include" });
    if (res.ok) setSelectedConv(await res.json());
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0 border-r border-border flex flex-col" style={{ background: "hsl(var(--sidebar-bg))" }}>
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-border">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
            <Shield className="h-3.5 w-3.5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>Admin Panel</span>
        </div>

        <nav className="flex-1 p-2 space-y-1">
          {[
            { id: "dashboard", label: "Přehled", icon: Activity },
            { id: "users", label: "Uživatelé", icon: Users },
            { id: "modules", label: "AI Moduly", icon: Package },
            { id: "settings", label: "API Nastavení", icon: Key },
            { id: "performance", label: "Výkon", icon: Gauge },
            { id: "conversations", label: "Konverzace", icon: MessageCircle },
            { id: "payments", label: "Platby", icon: CreditCard },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeTab === id
                  ? "bg-primary/10 text-foreground border border-primary/20"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>

        <div className="p-2 border-t border-border space-y-1">
          <Button variant="ghost" className="w-full justify-start gap-2 h-8 text-xs" onClick={() => navigate("/chat")}>
            <ArrowLeft className="h-3.5 w-3.5" />
            Zpět do chatu
          </Button>
          <Button variant="ghost" className="w-full justify-start gap-2 h-8 text-xs text-muted-foreground" onClick={handleLogout}>
            <LogOut className="h-3.5 w-3.5" />
            Odhlásit se
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background/95 backdrop-blur-sm z-10">
          <h1 className="text-xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
            {activeTab === "dashboard" && "Přehled"}
            {activeTab === "users" && "Správa uživatelů"}
            {activeTab === "modules" && "AI Moduly"}
            {activeTab === "settings" && "API Nastavení"}
            {activeTab === "performance" && "Nastavení výkonu"}
            {activeTab === "conversations" && "Konverzace"}
            {activeTab === "payments" && "Platby"}
          </h1>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <div className="text-sm text-muted-foreground">
              {user?.name}
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* DASHBOARD */}
          {activeTab === "dashboard" && (
            <div className="space-y-6 fade-in" data-testid="dashboard-tab">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Celkem uživatelů" value={stats?.total_users} icon={Users} />
                <StatCard title="Aktivní uživatelé" value={stats?.active_users} icon={Activity} />
                <StatCard title="Konverzace" value={stats?.total_conversations} icon={MessageCircle} />
                <StatCard title="Odeslaných zpráv" value={stats?.total_messages} icon={MessageSquare} />
              </div>
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Rychlý přehled</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Admin uživatelé</span>
                      <span className="font-medium">{stats?.admin_users ?? 0}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border">
                      <span className="text-muted-foreground">Běžní uživatelé</span>
                      <span className="font-medium">{(stats?.total_users ?? 0) - (stats?.admin_users ?? 0)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Průměr zpráv/uživatel</span>
                      <span className="font-medium">
                        {stats?.total_users ? Math.round((stats?.total_messages ?? 0) / stats.total_users) : 0}
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Průměr konverzací/uživatel</span>
                      <span className="font-medium">
                        {stats?.total_users ? Math.round((stats?.total_conversations ?? 0) / stats.total_users) : 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* USERS */}
          {activeTab === "users" && (
            <div className="space-y-4 fade-in" data-testid="users-tab">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{users.length} uživatelů celkem</p>
                <Button
                  size="sm"
                  className="gap-2 bg-primary hover:bg-primary/90 h-8 text-xs"
                  onClick={() => { setEditingUser(null); setUserDialog(true); }}
                  data-testid="create-user-button"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Přidat uživatele
                </Button>
              </div>

              <Card className="border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Jméno / E-mail</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Kredity</TableHead>
                      <TableHead>Zprávy</TableHead>
                      <TableHead>Stav</TableHead>
                      <TableHead>Poskytovatel</TableHead>
                      <TableHead className="text-right">Akce</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.role === "admin" ? "default" : "outline"} className="text-xs">
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{u.credits === -1 ? "∞" : u.credits}</TableCell>
                        <TableCell className="text-sm">{u.messages_used ?? 0}</TableCell>
                        <TableCell>
                          <Badge variant={u.is_active !== false ? "outline" : "destructive"} className="text-xs">
                            {u.is_active !== false ? "Aktivní" : "Zakázán"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground capitalize">{u.auth_provider || "email"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => { setEditingUser(u); setUserDialog(true); }}
                              data-testid={`edit-user-${u.user_id}`}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            {u.role !== "admin" && (
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7 hover:text-destructive"
                                onClick={() => handleDeleteUser(u.user_id)}
                                data-testid={`delete-user-${u.user_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {/* MODULES */}
          {activeTab === "modules" && (
            <div className="space-y-4 fade-in" data-testid="modules-tab">
              <p className="text-sm text-muted-foreground">
                Povolte a nakonfigurujte poskytovatele AI. Použijte Emergent Universal Key (kredity v ceně) nebo přidejte vlastní API klíče.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {modules.map((mod) => (
                  <ModuleCard
                    key={mod.module_id}
                    module={mod}
                    onSave={(update) => handleUpdateModule(mod.module_id, update)}
                    onTest={() => handleTestModule(mod.module_id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* SETTINGS */}
          {activeTab === "settings" && (
            <div className="max-w-2xl space-y-6 fade-in" data-testid="settings-tab">
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Key className="h-4 w-4 text-primary" />
                    Konfigurace OpenAI
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={settings.use_emergent_key}
                      onCheckedChange={(v) => setSettings({ ...settings, use_emergent_key: v })}
                      data-testid="use-emergent-key-switch"
                    />
                    <div>
                      <Label>Použít Emergent Universal Key</Label>
                      <p className="text-xs text-muted-foreground">Používá vestavěný klíč. Kredity se odečítají z vašeho Emergent zůstatku.</p>
                    </div>
                  </div>
                  {!settings.use_emergent_key && (
                    <div className="space-y-1.5">
                      <Label>Vlastní OpenAI API klíč</Label>
                      <div className="relative">
                        <Input
                          type={showApiKey ? "text" : "password"}
                          value={settings.openai_api_key || ""}
                          onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
                          placeholder="sk-..."
                          className="pr-10"
                          data-testid="openai-key-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                        >
                          {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label>Výchozí model</Label>
                    <Select value={settings.default_model} onValueChange={(v) => setSettings({ ...settings, default_model: v })}>
                      <SelectTrigger data-testid="default-model-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["gpt-4o", "gpt-4.1", "gpt-4", "gpt-3.5-turbo", "gpt-5.2"].map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" />
                    Ollama (místní modely)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={settings.ollama_enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, ollama_enabled: v })}
                      data-testid="ollama-enabled-switch"
                    />
                    <div>
                      <Label>Povolit Ollama</Label>
                      <p className="text-xs text-muted-foreground">Připojte se k lokální instanci Ollama pro bezplatné modely.</p>
                    </div>
                  </div>
                  {settings.ollama_enabled && (
                    <div className="space-y-1.5">
                      <Label>URL Ollama serveru</Label>
                      <Input
                        value={settings.ollama_url || "http://localhost:11434"}
                        onChange={(e) => setSettings({ ...settings, ollama_url: e.target.value })}
                        placeholder="http://localhost:11434"
                        data-testid="ollama-url-input"
                      />
                      <p className="text-xs text-muted-foreground">URL vašeho Ollama serveru (může být vzdálený)</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Button
                className="w-full bg-primary hover:bg-primary/90 gap-2"
                onClick={handleSaveSettings}
                disabled={settingsLoading}
                data-testid="save-settings-button"
              >
                {settingsLoading ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Uložit nastavení
              </Button>
            </div>
          )}

          {/* CONVERSATIONS */}
          {activeTab === "conversations" && (
            <div className="space-y-4 fade-in" data-testid="conversations-tab">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{conversations.length} konverzací celkem</p>
                <Button variant="outline" size="sm" className="gap-2 h-8 text-xs" onClick={fetchConversations}>
                  <RefreshCw className="h-3.5 w-3.5" />
                  Obnovit
                </Button>
              </div>
              <Card className="border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Název</TableHead>
                      <TableHead>ID uživatele</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Zprávy</TableHead>
                      <TableHead>Poslední změna</TableHead>
                      <TableHead className="text-right">Akce</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conversations.map((conv) => (
                      <TableRow key={conv.conversation_id} data-testid={`conv-row-${conv.conversation_id}`}>
                        <TableCell className="max-w-[200px]">
                          <p className="truncate text-sm font-medium">{conv.title || "Untitled"}</p>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">{conv.user_id}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{conv.model || "—"}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{conv.message_count || 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost" size="sm" className="h-7 text-xs gap-1"
                            onClick={() => openConvDetail(conv.conversation_id)}
                            data-testid={`view-conv-${conv.conversation_id}`}
                          >
                            Zobrazit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}

          {/* PERFORMANCE */}
          {activeTab === "performance" && (
            <div className="space-y-5 fade-in" data-testid="performance-tab">
              {/* Server info */}
              {serverInfo && !serverInfo.error && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                  <Card className="border-border p-4 flex items-center gap-3">
                    <Cpu className="h-5 w-5 text-primary flex-shrink-0" />
                    <div><p className="text-xs text-muted-foreground">CPU jader</p><p className="text-xl font-bold">{serverInfo.cpu_cores}</p><p className="text-xs text-muted-foreground">{serverInfo.cpu_usage}% využití</p></div>
                  </Card>
                  <Card className="border-border p-4 flex items-center gap-3">
                    <Server className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div><p className="text-xs text-muted-foreground">RAM celkem</p><p className="text-xl font-bold">{serverInfo.ram_total_gb} GB</p><p className="text-xs text-muted-foreground">{serverInfo.ram_available_gb} GB volné</p></div>
                  </Card>
                  <Card className="border-border p-4 flex items-center gap-3">
                    <TrendingUp className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    <div><p className="text-xs text-muted-foreground">Swap</p><p className="text-xl font-bold">{serverInfo.swap_total_gb} GB</p><p className="text-xs text-muted-foreground">{serverInfo.swap_used_gb} GB využito</p></div>
                  </Card>
                  <Card className="border-border p-4 flex items-center gap-3">
                    <BarChart3 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <div><p className="text-xs text-muted-foreground">Disk volný</p><p className="text-xl font-bold">{serverInfo.disk_free_gb} GB</p><p className="text-xs text-muted-foreground">z {serverInfo.disk_total_gb} GB</p></div>
                  </Card>
                </div>
              )}

              {/* Quick presets */}
              <Card className="border-border">
                <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4 text-primary" />Rychlé předvolby</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { id: "economy", label: "Úsporný", desc: "Min. RAM, pomalejší", color: "border-blue-500/30" },
                      { id: "balanced", label: "Vyvážený", desc: "Doporučeno", color: "border-primary/30" },
                      { id: "maximum", label: "Maximální", desc: "Velký kontext", color: "border-orange-500/30" },
                      { id: "turbo", label: "Turbo", desc: "Rychlé kratší odpovědi", color: "border-purple-500/30" },
                    ].map(preset => (
                      <button
                        key={preset.id}
                        onClick={() => handleApplyPreset(preset.id)}
                        disabled={perfSaving}
                        className={`p-3 rounded-xl border ${preset.color} ${perfSettings.preset === preset.id ? "ring-2 ring-primary/40 bg-primary/5" : "bg-card hover:bg-muted"} transition-all text-left`}
                        data-testid={`preset-${preset.id}`}
                      >
                        <p className="font-semibold text-sm">{preset.label}</p>
                        <p className="text-xs text-muted-foreground">{preset.desc}</p>
                        {perfSettings.preset === preset.id && <p className="text-xs text-primary mt-1 font-medium">● Aktivní</p>}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Manual settings */}
              <Card className="border-border">
                <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Settings className="h-4 w-4 text-primary" />Ollama – ruční nastavení</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { key: "ollama_num_threads", label: "CPU vláken", min: 1, max: 32, tip: "Více = rychlejší. Max = počet CPU jader serveru." },
                      { key: "ollama_num_ctx", label: "Délka kontextu", min: 512, max: 8192, tip: "Menší = rychlejší. 2048 = doporučeno." },
                      { key: "ollama_num_predict", label: "Max tokenů odpovědi", min: 128, max: 4096, tip: "Délka max. odpovědi. 1024 = doporučeno." },
                    ].map(({ key, label, min, max, tip }) => (
                      <div key={key} className="space-y-1.5">
                        <Label className="text-xs">{label}: <span className="font-bold text-primary">{perfSettings[key]}</span></Label>
                        <input
                          type="range" min={min} max={max} step={key === "ollama_num_threads" ? 1 : 256}
                          value={perfSettings[key]}
                          onChange={(e) => setPerfSettings(prev => ({ ...prev, [key]: parseInt(e.target.value), preset: "custom" }))}
                          className="w-full accent-primary"
                          data-testid={`perf-${key}`}
                        />
                        <p className="text-xs text-muted-foreground">{tip}</p>
                      </div>
                    ))}
                    <div className="space-y-1.5">
                      <Label className="text-xs">Teplota: <span className="font-bold text-primary">{perfSettings.ollama_temperature}</span></Label>
                      <input type="range" min={0.1} max={1.0} step={0.1} value={perfSettings.ollama_temperature}
                        onChange={(e) => setPerfSettings(prev => ({ ...prev, ollama_temperature: parseFloat(e.target.value), preset: "custom" }))}
                        className="w-full accent-primary" />
                      <p className="text-xs text-muted-foreground">0.1 = přesné, 1.0 = kreativní</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">OpenAI max tokenů: <span className="font-bold text-primary">{perfSettings.openai_max_tokens}</span></Label>
                      <input type="range" min={256} max={8192} step={256} value={perfSettings.openai_max_tokens}
                        onChange={(e) => setPerfSettings(prev => ({ ...prev, openai_max_tokens: parseInt(e.target.value), preset: "custom" }))}
                        className="w-full accent-primary" />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-5">
                    <Button className="flex-1 bg-primary hover:bg-primary/90 gap-2" onClick={handleSavePerf} disabled={perfSaving} data-testid="save-performance">
                      {perfSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Uložit nastavení výkonu
                    </Button>
                    <Button variant="outline" className="gap-2" onClick={handleBenchmark} disabled={benchmarking} data-testid="run-benchmark">
                      {benchmarking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Timer className="h-4 w-4" />}
                      Benchmark
                    </Button>
                    <Button variant="ghost" size="icon" onClick={fetchServerInfo} title="Obnovit info serveru">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Benchmark results */}
              {benchmarkResults && (
                <Card className="border-border fade-in">
                  <CardHeader className="pb-3"><CardTitle className="text-base">Výsledky benchmarku</CardTitle></CardHeader>
                  <CardContent>
                    {benchmarkResults.error ? (
                      <p className="text-sm text-destructive">{benchmarkResults.error}</p>
                    ) : (
                      <div className="space-y-3">
                        {benchmarkResults.results?.map((r, i) => (
                          <div key={i} className="flex items-center justify-between border border-border rounded-lg p-3">
                            <div>
                              <p className="font-medium text-sm">{r.model}</p>
                              {r.error ? <p className="text-xs text-destructive">{r.error}</p> : (
                                <p className="text-xs text-muted-foreground">{r.time_s}s celkově, {r.tokens} tokenů</p>
                              )}
                            </div>
                            {!r.error && (
                              <div className="text-right">
                                <p className="text-2xl font-bold text-primary">{r.tokens_per_sec}</p>
                                <p className="text-xs text-muted-foreground">tokenů/s</p>
                              </div>
                            )}
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground">Nastavení: {perfSettings.ollama_num_threads} vláken, ctx={perfSettings.ollama_num_ctx}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* PAYMENTS */}
          {activeTab === "payments" && (
            <div className="space-y-4 fade-in" data-testid="payments-tab">
              <div className="flex items-center justify-between">
                <div className="flex gap-4 text-sm">
                  <span className="text-muted-foreground">{payments.length} transakcí</span>
                  <span className="text-primary font-medium">
                    {payments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0).toLocaleString("cs-CZ")} Kč celkem
                  </span>
                </div>
                <Button variant="outline" size="sm" className="gap-2 h-8 text-xs" onClick={fetchPayments}>
                  <RefreshCw className="h-3.5 w-3.5" />Obnovit
                </Button>
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Zaplaceno", status: "paid", color: "text-green-500" },
                  { label: "Čeká", status: "pending", color: "text-orange-500" },
                  { label: "Celkem kreditů prodáno", status: "paid", color: "text-primary", isCredits: true },
                ].map(({ label, status, color, isCredits }) => (
                  <Card key={label} className="border-border p-4">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className={`text-2xl font-bold ${color}`}>
                      {isCredits
                        ? payments.filter(p => p.payment_status === status).reduce((s, p) => s + (p.credits || 0), 0).toLocaleString("cs-CZ")
                        : payments.filter(p => p.payment_status === status).length
                      }
                    </p>
                  </Card>
                ))}
              </div>

              <Card className="border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Uživatel</TableHead>
                      <TableHead>Balíček</TableHead>
                      <TableHead>Částka</TableHead>
                      <TableHead>Kredity</TableHead>
                      <TableHead>Stav</TableHead>
                      <TableHead>Datum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((tx) => (
                      <TableRow key={tx.transaction_id}>
                        <TableCell className="text-xs font-mono text-muted-foreground">{tx.user_id?.slice(-8)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs capitalize">
                            {{ starter: "Starter", business: "Business", pro: "Pro" }[tx.package_id] || tx.package_id}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-sm">{tx.amount?.toLocaleString("cs-CZ")} Kč</TableCell>
                        <TableCell className="text-primary font-semibold">+{tx.credits?.toLocaleString("cs-CZ")}</TableCell>
                        <TableCell>
                          <Badge variant={tx.payment_status === "paid" ? "default" : "secondary"} className="text-xs">
                            {tx.payment_status === "paid" ? "✓ Zaplaceno" : tx.payment_status === "pending" ? "Čeká" : tx.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString("cs-CZ") : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {payments.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Zatím žádné transakce</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Dialogs */}
      <UserDialog
        open={userDialog}
        onClose={() => { setUserDialog(false); setEditingUser(null); }}
        onSave={editingUser ? handleUpdateUser : handleCreateUser}
        editUser={editingUser}
      />
      <ConvDetailDialog conv={selectedConv} onClose={() => setSelectedConv(null)} />
    </div>
  );
}
